import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, Form, Row, Select, message } from "antd";
import { useFieldArray, useForm } from "react-hook-form";
import { difference } from "lodash";

import {
  CardElement,
  CenteredInputContainer,
  HMGSelect,
  ProjectSelect,
} from "../../components/Common";
import {
  useGetProjectDistinctReportNames,
  useGetProjectHotels,
  useGetProjectReportCategories,
  useGetProjectReports,
} from "../../hooks/project.hooks";
import { useReportUpdateReportCategory } from "../../hooks/report.hooks";
import UpdateReportCategoryRow from "../../components/ReportCategory/UpdateBulkReports/UpdateReportCategoryRow";

const { Option } = Select;



const UpdateBulkReports = () => {
  const history = useHistory();
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [reportName, setReportName] = useState();
  const [showStatus, setShowStatus] = useState(false);
  const [categoryUpdated, setCategoryUpdated] = useState(false);

  const [projectList, setProjectList] = useState();

  const setHmgRef = useCallback((list) => {
    if (list?.length === 1) setHmg(list[0]._id);
  }, []);

  const setProjectRef = useCallback((list) => {
    setProjectList(list);
  }, []);

  useEffect(() => {
    setReportName(undefined);
  }, [project]);

  useEffect(() => {
    setProject(
      projectList?.length === 1 && hmg ? projectList[0]._id : undefined,
    );
  }, [projectList, hmg]);

  const { data: projectReportCategories } = useGetProjectReportCategories(
    project,
    { enabled: !!project },
  );

  const { data: projectReportNames, isLoading: isLoadingReportNames } =
    useGetProjectDistinctReportNames(project, {
      enabled: !!project,
      select: (data) =>
        data.sort((value1, value2) => value1.localeCompare(value2)),
    });

  const { data: projectHotels } = useGetProjectHotels(project, {
    enabled: !!project,
    initialData: [],
  });

  const mappedHotelData = useMemo(() => {
    return new Map(projectHotels.map((hotel) => [hotel._id, hotel.hotel_name]));
  }, [projectHotels]);

  const {
    data: reportData,
    isLoading: isLoadingReportList,
    refetch: refetchReports,
  } = useGetProjectReports(
    project,
    { ...(reportName ? { ReportName: reportName } : {}) },
    {
      enabled: false,
    },
  );

  const { mutateAsync: updateReportCategory, isLoading: isUpdatingReports } =
    useReportUpdateReportCategory({});

  const { control, resetField, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      reports: [],
    },
  });

  const { fields, replace, remove } = useFieldArray({
    control,
    name: "reports",
  });

  const onSubmit = async (data) => {
    setShowStatus(true);
    
    for (let i = 0; i < data.reports.length; i++) {
      const record = data.reports[i];
      if (
        record.current.length !== record.report_category.length ||
        difference(record.report_category, record.current).length !== 0
      ) {
        try {
          await updateReportCategory({
            id: record.report_id,
            data: { report_category: record.report_category },
          });
          setValue(`reports.${i}.message`, "Successfully Updated");
        } catch (error) {
          setValue(
            `reports.${i}.message`,
            error.response?.data?.message ?? "Server Error Occured",
          );
        }
      }
    }
    setTimeout(() => {
      setHmg(undefined);
    }, 1000);
  };

  useEffect(() => {
    setShowStatus(false);
    if (reportName) {
      refetchReports();
    } else {
      resetField("reports");
    }
  }, [reportName]);

  useEffect(() => {
    const subscription = watch((data) => {
      let changeOccurred = false;
      if (Array.isArray(data.reports)) {
        changeOccurred = data.reports.some((report) => {
          return (
            report.current.length !== report.report_category.length ||
            difference(report.report_category, report.current).length !== 0
          );
        });
      }
      setCategoryUpdated(changeOccurred);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    let formDataArray = [];
    if (reportData) {
      formDataArray = reportData
        .filter((report) => mappedHotelData.has(report.hotel_id))
        .map((report) => ({
          report_id: report._id,
          hotel_name: mappedHotelData.get(report.hotel_id),
          current: report.report_category,
          report_category: report.report_category,
          message: "",
        }))
        .sort((element1, element2) => {
          const hotelName1 = element1.hotel_name.toUpperCase();
          const hotelName2 = element2.hotel_name.toUpperCase();
          if (hotelName1 < hotelName2) {
            return -1;
          }
          if (hotelName1 > hotelName2) {
            return 1;
          }
          return 0;
        });
    }
    replace(formDataArray);
  }, [reportData]);

  return (
    <CardElement
      title="Report Category"
      subHeading="Edit"
      showClose
      onClose={() => history.push("/report-category/view")}
    >
      <CenteredInputContainer>
        <Form wrapperCol={{ span: 24 }} labelCol={{ span: 24 }}>
          <Form.Item label="Hotel Management Group">
            <HMGSelect
              value={hmg}
              onChange={setHmg}
              placeholder="Hotel Management Group"
              setHmgListRef={setHmgRef}
            />
          </Form.Item>
          <Form.Item label="Project">
            <ProjectSelect
              value={project}
              onChange={setProject}
              hmgId={hmg}
              placeholder="Project"
              disabled={!hmg}
              setProjectListRef={setProjectRef}
            />
          </Form.Item>
          <Form.Item label="Report Name">
            <Select
              placeholder="Report name"
              value={reportName}
              onChange={setReportName}
              loading={isLoadingReportNames}
              showSearch
              optionFilterProp="children"
              disabled={!project}
            >
              {Array.isArray(projectReportNames) &&
                projectReportNames.map((name) => (
                  <Option value={name} key={name}>
                    {name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </CenteredInputContainer>
      <div
        style={{
          overflowX: "auto",
        }}
      >
        {fields.map((report, index) => (
          <UpdateReportCategoryRow
            {...(index === 0 ? { showLabels: true } : {})}
            reportCategories={projectReportCategories ?? []}
            key={report.id}
            control={control}
            index={index}
            remove={remove}
            showMessage={showStatus}
          />
        ))}
      </div>
      <CenteredInputContainer>
        <Form style={{ marginTop: 10 }}>
          <Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Button block onClick={() => setHmg(undefined)}>
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  block
                  type="primary"
                  disabled={fields.length === 0 || !categoryUpdated}
                  onClick={handleSubmit(onSubmit)}
                  loading={isUpdatingReports}
                >
                  Update
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </CenteredInputContainer>
    </CardElement>
  );
};

export default UpdateBulkReports;
